import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Grid,
  GridProps as MuiGridProps,
  IconButton,
  SvgIconProps,
  Tooltip,
  TooltipProps,
} from "@mui/material";
import { format } from "date-fns";
import kebabCase from "lodash/kebabCase";
import { JSX, ReactElement, ReactNode } from "react";
import { useNotification } from "@platform/hooks";
import { logger } from "@platform/browser-logger";
import { Typography } from "@platform/design-system";

import { FieldsData } from "../../types";

export type Props = {
  data: (FieldsData & {
    element?: ReactNode;
  })[];
  gridItemProps?: GridProps;
  gridContainerProps?: GridProps;
};

const Fields = (props: Props): JSX.Element => (
  <Grid
    data-testid="ds-fields"
    container
    spacing={3}
    {...props.gridContainerProps}
  >
    {props.data.map((field) => {
      const { label, value, element, gridItemProps, inputType, inputProps } =
        field as FieldsData & { element?: ReactNode };
      const key = typeof label === "string" ? label : label.name;

      // Handle grouped fields in view mode
      if (inputType === "group" && inputProps?.fields) {
        if (typeof value === "string") {
          return (
            <Grid
              data-testid={`ds-field-${kebabCase(key)}`}
              key={key}
              {...props.gridItemProps}
              {...gridItemProps}
            >
              {element ?? <Value label={label} value={value} />}
            </Grid>
          );
        }

        return (
          <Grid
            data-testid={`ds-field-${kebabCase(key)}`}
            key={key}
            {...props.gridItemProps}
            {...gridItemProps}
          >
            {element ?? (
              <>
                <Typography
                  variant="body-medium"
                  data-testid={`ds-field-label-${kebabCase(key)}`}
                  display="flex"
                  alignItems="center"
                  columnGap={1.25}
                  sx={{ mb: 1 }}
                >
                  <strong>
                    {typeof label === "string" ? label : label.name}
                  </strong>
                </Typography>
                <Grid
                  container
                  spacing={inputProps.grid?.spacing || 0}
                  columns={inputProps.grid?.columns || 12}
                >
                  {inputProps.fields.map((subField, index) => {
                    if (
                      subField.hiddenOnEmptyViewMode &&
                      (subField.value === "" || subField.value == null)
                    ) {
                      return null;
                    }

                    const subKey = subField.label
                      ? typeof subField.label === "string"
                        ? subField.label
                        : subField.label.name
                      : `sub-field-${index}`;
                    return (
                      <Grid
                        key={subKey}
                        data-testid={`ds-field-group-item-${kebabCase(subKey)}`}
                        {...subField.gridItemProps}
                      >
                        <Value
                          label={
                            subField.labelHiddenOnViewMode || !subField.label
                              ? null
                              : subField.label
                          }
                          value={subField.value}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </Grid>
        );
      }

      // Handle regular fields
      return (
        <Grid
          data-testid={`ds-field-${kebabCase(key)}`}
          key={key}
          {...props.gridItemProps}
          {...gridItemProps}
        >
          {element ? element : <Value label={label} value={value} />}
        </Grid>
      );
    })}
  </Grid>
);

function Value({
  label,
  value,
}: {
  label: FieldsData["label"] | null;
  value: FieldsData["value"];
}): JSX.Element {
  const { showSuccess } = useNotification();

  let labelElement: ReactElement | null = null;
  let valueDataTestId = "ds-field-value";

  const handleCopy = (): void => {
    if (typeof value !== "string") return;
    if (!navigator?.clipboard?.writeText) return;
    void navigator.clipboard.writeText(value).catch((err) => {
      logger.error("Failed to copy text: ", err);
    });
    showSuccess("Copied to clipboard");
  };

  if (label !== null) {
    const key = typeof label === "string" ? label : label.name;
    valueDataTestId = `${valueDataTestId}-${kebabCase(key)}`;
    labelElement = (
      <Typography
        variant="body-medium"
        data-testid={`ds-field-label-${kebabCase(key)}`}
        display="flex"
        alignItems="center"
        columnGap={1.25}
      >
        {typeof label === "string" ? (
          <strong>{label}</strong>
        ) : (
          <>
            <strong>{label.name}</strong>
            {label.tooltip ? (
              <InfoTooltip
                tooltipProps={{ title: label.tooltip }}
                iconProps={{ sx: { fontSize: 18 } }}
              />
            ) : null}
            {label.icon}
            {label.allowCopy && (
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  aria-label={`Copy ${label.name} value`}
                  onClick={handleCopy}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Typography>
    );
  }

  return (
    <>
      {labelElement}
      {typeof value === "string" ? (
        <Typography
          data-testid={valueDataTestId}
          sx={{ overflowWrap: "break-word", whiteSpace: "pre-wrap" }}
        >
          {value === "" ? "-" : value}
        </Typography>
      ) : value instanceof Date ? (
        <Typography data-testid={valueDataTestId}>
          {format(value, "MM/dd/yyyy")}
        </Typography>
      ) : Array.isArray(value) ? (
        <Box data-testid={valueDataTestId}>
          {value.map((v: string, i) => (
            // eslint-disable-next-line react/no-array-index-key -- values may repeat; index needed to ensure uniqueness
            <Typography key={`${v}-${i}`}>{v}</Typography>
          ))}
        </Box>
      ) : (
        (value ?? "-")
      )}
    </>
  );
}

function InfoTooltip(props: {
  tooltipProps: Omit<TooltipProps, "children">;
  iconProps?: SvgIconProps;
}): JSX.Element {
  return (
    <Tooltip {...props.tooltipProps}>
      <InfoOutlinedIcon cursor="pointer" {...props.iconProps} />
    </Tooltip>
  );
}

type GridProps = Omit<MuiGridProps, "container">;

export default Fields;
export { Fields };
